import { useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { SidebarToolbar } from "@/components/sidebar/SidebarToolbar";
import { groupsJson } from "@/components/suggestions/providers/data";
import { twMerge } from "@/utils/twMerge";

import type { GroupTypeValue } from "@/components/suggestions/providers/data/types";
import type { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import type { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import type { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

type Props = {
  node: GroupNodeModel | VariableNodeModel | FunctionNodeModel;
  displayLimit?: number;
  className?: string;
  onSelect?: (group: (typeof groupsJson)[GroupTypeValue]) => void;
};

export const BracketsSelector = observer(function BracketsSelector({
  node,
  displayLimit,
  className,
  onSelect = () => {},
}: Props) {
  const { doc } = node;

  const keys = Object.keys(groupsJson) as GroupTypeValue[];
  const limit = displayLimit === undefined ? keys.length : displayLimit;
  const isExpandable = limit === undefined || limit >= keys.length;

  const [isOpen, setIsOpen] = useState(isExpandable);
  const t = useTranslations();

  const handleOnClick = (group: (typeof groupsJson)[GroupTypeValue]) => {
    doc.transact(() => {
      node.openingSymbol = group.openingSymbol.symbol;
      node.closingSymbol = group.closingSymbol.symbol;
    }, "bracketsSelector:select");
    doc.focus();
    onSelect(group);
  };

  const visibleGroups = isOpen ? keys : keys.slice(0, limit < 0 ? 0 : limit);

  return (
    <SidebarToolbar
      label={t("sidebar.activeNode.group.title.selector")}
      className={twMerge("grid grid-cols-6 gap-[6px]", className)}
    >
      {/* `contents`: the radios stay cells of the toolbar's grid */}
      <div role="radiogroup" aria-label={t("sidebar.toolbars.brackets")} className="contents">
        {visibleGroups.map((key) => {
          const group = groupsJson[key];
          const isActive =
            node.openingSymbol === group.openingSymbol.symbol &&
            node.closingSymbol === group.closingSymbol.symbol;

          return (
            <Sidebar.Button
              key={key}
              inverted
              isActive={isActive}
              role="radio"
              aria-checked={isActive}
              aria-label={t(`suggestions.${group.intlName}`)}
              onClick={() => handleOnClick(group)}
              className="min-h-[34px] max-w-[41px]"
              icon={<Icon name={group.icon} />}
            />
          );
        })}
      </div>
      {!isExpandable && (
        <Sidebar.Button
          inverted
          title={t(`sidebar.actions.${isOpen ? "hide" : "more"}`)}
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          className="min-h-[34px] max-w-[41px]"
          icon={<Icon name="ChevronDown" className={twMerge(isOpen && "rotate-180")} />}
        />
      )}
    </SidebarToolbar>
  );
});
